import {
 JSX
} from 'react';
import {
    Card, CardContent
} from 'shared/components/ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';

// Define the types for the props and nested objects
interface FieldSettings {
    [fieldName: string]: 'visible' | 'hidden' | 'readonly' | undefined;
}

interface SelectedItemConfig {
    selectedFields: string[];
    selectedConnectedFields: string[];
    selectedMainTable: string;
    fieldSettings: FieldSettings;
}

interface SelectedItem {
    config: SelectedItemConfig;
    databaseSchema: {
        tables: Record<string, any>; // Replace 'any' with the actual schema structure
    };
}

interface OpCodePreviewProps {
    selectedItem: SelectedItem | null;
}

export default function OpCodePreview({
    selectedItem
}: OpCodePreviewProps): JSX.Element {
    if (!selectedItem || !selectedItem.config || !selectedItem.databaseSchema) {
        return <div className="text-muted-foreground p-4">No data to preview.</div>;
    }

    const {
        selectedFields = [],
        selectedConnectedFields = [],
        selectedMainTable,
        fieldSettings = {}
    } = selectedItem.config;

    if (!selectedMainTable) {
        return <div className="text-muted-foreground p-4">No schema generated yet.</div>;
    }

    // Function to format field names
    const getLabel = (fieldName: string): string => {
        const rawField = fieldName.includes('.')
            ? fieldName.split('.')[1] // Get only the field name part
            : fieldName;

        return rawField
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase -> space
            .replace(/_/g, ' ') // snake_case -> space
            .replace(/^./, (s) => s.toUpperCase()); // Capitalize first letter
    };

    // Function to render a table with fields
    const renderTable = (title: string, fields: string[]) => (
        <Card className="pt-8" key={title}>
            <CardContent>
                <h2 className="text-lg font-semibold mb-4">{title}</h2>
                <div className="w-full max-w-full overflow-x-auto">
                    <Table className="w-full table-auto">
                        <TableHeader>
                            <TableRow>
                                {fields.map((field, index) => (
                                    <TableHead
                                        key={index}
                                        className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]"
                                        title={field}
                                    >
                                        {getLabel(field)}
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow>
                                {fields.map((field, index) => (
                                    <TableCell
                                        key={index}
                                        className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]"
                                    >
                                        Sample data
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );

    // Filter out hidden fields from selected fields
    const visibleMainFields = selectedFields.filter(
        (fieldName) => fieldSettings[fieldName] !== 'hidden'
    );

    const connectedTableMap: Record<string, string[]> = {};

    selectedConnectedFields.forEach((field) => {
        // eslint-disable-next-line no-unused-vars
        const [table] = field.split('.');
        if (fieldSettings[field] !== 'hidden') {
            if (!connectedTableMap[table]) connectedTableMap[table] = [];
            connectedTableMap[table].push(field);
        }
    });

    return (
        <div className="flex flex-col gap-4">
            {/* Main table */}
            {renderTable(selectedMainTable, visibleMainFields)}

            {/* Connected tables */}
            {Object.entries(connectedTableMap).map(([tableName, fields]) =>
                renderTable(tableName, fields)
            )}
        </div>
    );
}
